import type { AdminFlowBlockSettingsProps } from "../../models/admin-flow-whiteboard.models";
import {
  ADMIN_FLOW_FIELD_CLASS,
  ADMIN_FLOW_KIND_LABELS,
  ADMIN_FLOW_SOURCE_OPTIONS,
  ADMIN_FLOW_SOURCE_ARGUMENT_HELP,
  ADMIN_FLOW_TOOLS_PER_NODE,
} from "../../utils/admin-flow-whiteboard.constants";
import { getAdminFlowSourceArguments } from "../../utils/admin-flow-whiteboard.utils";
import { getAdminFlowToolNames } from "../../utils/admin-flows.utils";
import AdminFlowToolMenu from "./AdminFlowToolMenu";
import { useEffect, useRef, useState } from "react";

// Edit a prepared block without deciding which flow owns it.
// Cards and Whiteboard use this same form so changing presentation cannot change the block contract.
const AdminFlowBlockSettings = ({ node, tools, onChange, onDelete }: AdminFlowBlockSettingsProps) => {
  const permittedStepTools = tools.filter((tool) => tool.executionPolicy !== "native_email_action");
  const selectedTool = permittedStepTools.find((tool) => tool.name === node.toolName);
  const toolNames = getAdminFlowToolNames(node);
  const [argumentText, setArgumentText] = useState(JSON.stringify(node.arguments, null, 2));
  const [argumentError, setArgumentError] = useState("");
  const submittedArguments = useRef(node.arguments);
  const unavailableTools = toolNames.filter((name) => !permittedStepTools.some((tool) => tool.name === name));
  const sourceDetails = ADMIN_FLOW_SOURCE_OPTIONS.find((source) => source.value === node.source);
  const fieldPrefix = `admin-flow-block-${node.id}`;

  // Reflect external edits and tool changes without keeping a second copy of the flow itself.
  useEffect(() => {
    // Preserve the user's spacing and caret when the parent echoes this form's own edit.
    if (node.arguments === submittedArguments.current) return;

    submittedArguments.current = node.arguments;
    setArgumentText(JSON.stringify(node.arguments, null, 2));
    setArgumentError("");
  }, [node.arguments]);

  const changeArguments = (text: string) => {
    setArgumentText(text);

    try {
      const parsed: unknown = JSON.parse(text);

      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error('Arguments must be a JSON object, for example {"limit": 10}.');
      }

      setArgumentError("");
      submittedArguments.current = parsed as Record<string, unknown>;
      onChange({ ...node, arguments: parsed as Record<string, unknown> });
    } catch (error) {
      setArgumentError(error instanceof Error ? error.message : "Enter a valid JSON object.");
    }
  };

  const changeTools = (names: string[]) => {
    onChange({
      ...node,
      toolName: names[0] ?? "",
      toolNames: names,
      arguments: names[0] === node.toolName ? node.arguments : {},
    });
  };

  const replaceTool = (index: number, name?: string) => {
    const names = [...toolNames];

    if (name) names[index] = name;
    else names.splice(index, 1);

    changeTools(names);
  };

  return (
    <section data-test="admin-flow-block-settings" className="space-y-4 text-sm">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-green">Block settings</p>
          <h3 className="mt-1 font-semibold">
            {node.kind === "agent" && !toolNames.length ? "AI processing" : ADMIN_FLOW_KIND_LABELS[node.kind]}
          </h3>
        </div>
        {onDelete && (
          <button
            type="button"
            data-test="admin-flow-block-delete"
            onClick={onDelete}
            className="rounded px-2 py-1 text-xs text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950"
          >
            Remove
          </button>
        )}
      </div>
      <label className="block" htmlFor={`${fieldPrefix}-label`}>
        Block name
        <input
          id={`${fieldPrefix}-label`}
          data-test="admin-flow-block-label"
          value={node.label}
          maxLength={120}
          onChange={(event) => onChange({ ...node, label: event.target.value })}
          className={ADMIN_FLOW_FIELD_CLASS}
        />
      </label>
      {node.kind === "source" && (
        <>
          <label className="block" htmlFor={`${fieldPrefix}-source`}>
            Data source
            <select
              id={`${fieldPrefix}-source`}
              data-test="admin-flow-block-source"
              value={node.source}
              onChange={(event) =>
                onChange({
                  ...node,
                  source: event.target.value,
                  arguments: getAdminFlowSourceArguments(event.target.value),
                })
              }
              className={ADMIN_FLOW_FIELD_CLASS}
            >
              <option value="">Choose a source</option>
              {ADMIN_FLOW_SOURCE_OPTIONS.map((source) => (
                <option key={source.value} value={source.value}>
                  {source.label}
                </option>
              ))}
            </select>
          </label>
          <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">
            {sourceDetails?.description}. Search criteria stay in this draft. Reusable saved search configurations will
            connect when that feature is available.
          </p>
          <p
            data-test="admin-flow-source-argument-help"
            className="rounded bg-[#E9F4F3] p-3 text-xs leading-5 text-green dark:bg-green/10"
          >
            {ADMIN_FLOW_SOURCE_ARGUMENT_HELP[node.source] || "Select an approved source to see its required criteria."}
          </p>
        </>
      )}
      {node.kind === "agent" && (
        <>
          <div className="space-y-1">
            <p className="text-sm">Tools (optional)</p>
            {toolNames.map((name, index) => (
              <div
                key={`${index}-${name}`}
                className="flex items-start gap-1"
                data-test={`admin-flow-attached-tool-${index}`}
              >
                <div className="min-w-0 flex-1">
                  <AdminFlowToolMenu
                    tools={permittedStepTools.filter((tool) => tool.name === name || !toolNames.includes(tool.name))}
                    value={name}
                    allowProcessing={false}
                    label={name}
                    testId={`admin-flow-block-tool-${index}`}
                    onSelect={(tool) => replaceTool(index, tool?.name)}
                  />
                </div>
                <button
                  type="button"
                  data-test={`admin-flow-remove-tool-${index}`}
                  aria-label={`Remove tool ${name}`}
                  className="rounded px-2 py-2 text-gray-500 hover:text-red-700"
                  onClick={() => changeTools(toolNames.filter((_, position) => position !== index))}
                >
                  ×
                </button>
              </div>
            ))}
            {toolNames.length < ADMIN_FLOW_TOOLS_PER_NODE && (
              <AdminFlowToolMenu
                tools={permittedStepTools.filter((tool) => !toolNames.includes(tool.name))}
                label="+ Add tool"
                allowProcessing={false}
                testId="admin-flow-add-block-tool"
                onSelect={(tool) => {
                  if (tool) changeTools([...toolNames, tool.name]);
                }}
              />
            )}
            <p className="text-xs leading-5 text-gray-500">
              {toolNames.length
                ? "The AI chooses which attached tools to call and in what order, then returns one answer for this prompt."
                : "Prompt-only processing. The AI uses connected inputs without calling tools."}
            </p>
          </div>
          {unavailableTools.length > 0 && (
            <p
              role="alert"
              data-test="admin-flow-block-tool-unavailable"
              className="text-xs text-amber-700 dark:text-amber-300"
            >
              Saved tools {unavailableTools.join(", ")} are unavailable under the current permissions. Their settings
              are retained.
            </p>
          )}
          {!permittedStepTools.length && toolNames.length > 0 && (
            <p className="text-xs text-gray-500">
              No permitted tools are available. Refresh the tool catalog from the workspace.
            </p>
          )}
        </>
      )}
      {(node.kind === "source" ||
        (node.kind === "agent" && toolNames.length > 0 && Object.keys(node.arguments).length > 0)) && (
        <label className="block" htmlFor={`${fieldPrefix}-arguments`}>
          {node.kind === "source" ? "Search criteria / source arguments" : "Saved argument hints for the first tool"}
          <textarea
            id={`${fieldPrefix}-arguments`}
            data-test="admin-flow-block-arguments"
            value={argumentText}
            maxLength={16000}
            rows={6}
            spellCheck={false}
            onChange={(event) => changeArguments(event.target.value)}
            aria-invalid={Boolean(argumentError)}
            aria-describedby={argumentError ? `${fieldPrefix}-argument-error` : undefined}
            className={`${ADMIN_FLOW_FIELD_CLASS} resize-y font-mono text-xs`}
          />
          {argumentError && (
            <span
              id={`${fieldPrefix}-argument-error`}
              data-test="admin-flow-block-arguments-error"
              role="alert"
              className="mt-1 block text-xs text-red-700 dark:text-red-300"
            >
              {argumentError} The last valid arguments remain in the draft.
            </span>
          )}
          {node.kind === "source" && selectedTool && selectedTool.inputSchema.required.length > 0 && (
            <span className="mt-1 block text-xs text-gray-500">
              Required fields: {selectedTool.inputSchema.required.join(", ")}
            </span>
          )}
        </label>
      )}
      {node.kind !== "merge" && (
        <label className="block" htmlFor={`${fieldPrefix}-instruction`}>
          {node.kind === "agent"
            ? "What should this AI step do?"
            : node.kind === "source"
              ? "Source notes"
              : "Output instructions"}
          <textarea
            id={`${fieldPrefix}-instruction`}
            data-test="admin-flow-block-instruction"
            value={node.instruction}
            maxLength={6000}
            rows={4}
            onChange={(event) => onChange({ ...node, instruction: event.target.value })}
            className={`${ADMIN_FLOW_FIELD_CLASS} resize-y`}
            placeholder={
              node.kind === "agent" ? "Describe the task using the connected inputs…" : "Describe the intended result…"
            }
          />
        </label>
      )}
      {node.kind === "merge" && (
        <p className="rounded bg-blue-50 p-3 text-xs leading-5 text-blue-900 dark:bg-blue-950 dark:text-blue-200">
          Wait for all connected inputs and pass their labelled results together to the next step. Steps run
          sequentially. Boolean gates will be added separately.
        </p>
      )}
      {node.kind === "email" && (
        <>
          <label className="block" htmlFor={`${fieldPrefix}-recipients`}>
            Recipients
            <input
              id={`${fieldPrefix}-recipients`}
              data-test="admin-flow-block-recipients"
              value={node.recipients}
              maxLength={2000}
              onChange={(event) => onChange({ ...node, recipients: event.target.value })}
              className={ADMIN_FLOW_FIELD_CLASS}
              placeholder="name@cargill.com"
            />
          </label>
          <label className="block" htmlFor={`${fieldPrefix}-subject`}>
            Subject
            <input
              id={`${fieldPrefix}-subject`}
              data-test="admin-flow-block-subject"
              value={node.subject}
              maxLength={200}
              onChange={(event) => onChange({ ...node, subject: event.target.value })}
              className={ADMIN_FLOW_FIELD_CLASS}
            />
          </label>
          <p className="rounded bg-amber-50 p-3 text-xs leading-5 text-amber-900 dark:bg-amber-950 dark:text-amber-200">
            This output prepares an email for review. It does not send mail or bypass the existing sender permissions.
          </p>
        </>
      )}
    </section>
  );
};

export default AdminFlowBlockSettings;
